import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import CashSale from './pages/CashSale';
import Refund from './pages/Refund';
import AccountInvoice from './pages/AccountInvoice';
import AccountCreditNote from './pages/AccountCreditNote';
import Quotes from './pages/Quotes';
import QuoteNew from './pages/QuoteNew';
import Search from './pages/Search';
import DailyReport from './pages/DailyReport';
import MonthlyReport from './pages/MonthlyReport';
import ManageAccounts from './pages/ManageAccounts';
import LoginPage from './pages/LoginPage';
import CashSaleViewer from './pages/viewers/CashSaleViewer';
import RefundViewer from './pages/viewers/RefundViewer';
import InvoiceViewer from './pages/viewers/InvoiceViewer';
import CreditNoteViewer from './pages/viewers/CreditNoteViewer';

import { useAuth } from './context/AuthContext';
import { ErrorBoundary } from './components/common';

function PrivateRoute({ children }) {
    const { user } = useAuth();
    return user ? children : <Navigate to="/login" replace />;
}

// Monthly Report and Manage Accounts were admin-only in the old Angular app
// (gated on currentUser.admin == 1) - this was lost in the React rewrite,
// where every logged-in user could reach them. Nests inside PrivateRoute's
// logged-in check, so a non-admin lands on Cash Sale rather than login.
function AdminRoute({ children }) {
    const { user, isAdmin } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    return isAdmin ? children : <Navigate to="/cash-sale" replace />;
}

function App() {
    const { user } = useAuth();
    // Deliberately plain useState, not persisted anywhere - the easter egg
    // is meant to clear on refresh, so in-memory-only is exactly right.
    const [easterEggActive, setEasterEggActive] = useState(false);

    // Warm the browser cache right after login rather than waiting for the
    // 7th logo click - the image is otherwise fetched for the first time at
    // the exact moment it needs to appear, which reads as a slow/broken reveal.
    useEffect(() => {
        if (user) {
            const img = new Image();
            img.src = '/easter_egg.jpg';
        }
    }, [user]);

    const mainStyle = {
        padding: '1rem',
        flexGrow: 1,
        overflowY: 'auto',
    };

    return (
        <ErrorBoundary>
            <Router>
                {/* height:100vh + minHeight:0 on the row below is what lets
                    only `main` scroll internally, regardless of how tall the
                    Topbar actually renders - no hardcoded pixel heights that
                    can drift out of sync with real content (e.g. the logo). */}
                <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Topbar onLogoEasterEgg={() => setEasterEggActive(true)} />

                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    {user && <Sidebar />}
                    {/* position:relative scopes the easter-egg overlay below to
                        exactly this box - the main content area - so it can
                        never cover the Sidebar or Topbar, which live outside it. */}
                    <div style={{ position: 'relative', flexGrow: 1, minWidth: 0, display: 'flex' }}>
                    <main style={mainStyle}>
                        <Routes>
                        <Route path="/login" element={<LoginPage />} />

                        {/* Protected routes */}
                        <Route
                            path="/cash-sale"
                            element={
                                <PrivateRoute>
                                    <CashSale />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/refund"
                            element={
                                <PrivateRoute>
                                    <Refund />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/account-invoice"
                            element={
                                <PrivateRoute>
                                    <AccountInvoice />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/account-credit-note"
                            element={
                                <PrivateRoute>
                                    <AccountCreditNote />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/quotes"
                            element={
                                <PrivateRoute>
                                    <Quotes />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/quotes/new"
                            element={
                                <PrivateRoute>
                                    <QuoteNew />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/search"
                            element={
                                <PrivateRoute>
                                    <Search />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/view/cash-sale/:id"
                            element={
                                <PrivateRoute>
                                    <CashSaleViewer />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/view/refund/:id"
                            element={
                                <PrivateRoute>
                                    <RefundViewer />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/view/invoice/:id"
                            element={
                                <PrivateRoute>
                                    <InvoiceViewer />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/view/credit-note/:id"
                            element={
                                <PrivateRoute>
                                    <CreditNoteViewer />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/daily-report"
                            element={
                                <PrivateRoute>
                                    <DailyReport />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/monthly-report"
                            element={
                                <AdminRoute>
                                    <MonthlyReport />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/manage-accounts"
                            element={
                                <AdminRoute>
                                    <ManageAccounts />
                                </AdminRoute>
                            }
                        />

                        {/* Default route */}
                        <Route path="/" element={<Navigate to="/cash-sale" replace />} />
                        </Routes>
                    </main>

                    {/* Absolute + a later sibling in a position:relative parent
                        is enough to stack above main's normal-flow content
                        (the tables/forms) without needing a z-index arms race
                        against the Sidebar/Topbar, which sit outside this box
                        entirely. pointer-events: none so it's a pure visual
                        gag, not something that traps the user out of the app. */}
                    {easterEggActive && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                backgroundColor: '#000',
                                backgroundImage: 'url(/easter_egg.jpg)',
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                pointerEvents: 'none',
                            }}
                        />
                    )}
                    </div>
                </div>
                </div>
            </Router>
        </ErrorBoundary>
    );
}

export default App;
